/** @jsx jsx */
import React, { useState, useCallback, type ReactNode } from 'react';
import { css, styled, jsx } from '@compiled/react';
import Button from '@atlaskit/button';
import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel';
import Popup from '@atlaskit/popup';
import { colors } from '@atlaskit/theme';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import { layers } from '@atlassian/jira-common-styles/src';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { useRequestTypeFieldsLayoutPreference } from '../../../../controllers/request-type-fields-layout-user-preference';
import messages from './messages';

export const RequestTypeFieldsLayoutPreference = ({
	isRequestTypeSelected,
	overrideIsEnabled = undefined,
	onChange: onChangeCallback,
}: {
	isRequestTypeSelected: boolean;
	overrideIsEnabled?: boolean;
	onChange: () => void;
}) => {
	const { atlassianAccountId } = useTenantContext();
	const [{ data: isEnabled, isLoading }, { setPreference }] =
		useRequestTypeFieldsLayoutPreference();

	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const [toggleWasClicked, setToggleWasClicked] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isChecked =
		!toggleWasClicked && overrideIsEnabled !== undefined ? !!overrideIsEnabled : isEnabled;

	const onToggleChanged = useCallback(() => {
		const newValue = !isChecked;
		setToggleWasClicked(true);
		atlassianAccountId &&
			setPreference(atlassianAccountId, newValue, createAnalyticsEvent, onChangeCallback);
	}, [atlassianAccountId, createAnalyticsEvent, isChecked, onChangeCallback, setPreference]);

	const togglePopup = useCallback(() => {
		if (!isOpen) {
			fireUIAnalytics(createAnalyticsEvent({}), 'gicRequestTypeToggleLabelButton clicked');
		}
		setIsOpen(!isOpen);
	}, [createAnalyticsEvent, isOpen]);

	const popupContent = useCallback(
		() => (
			<PopupContainer>
				<PopupParagraph>
					{formatMessage(
						isRequestTypeSelected
							? messages.createFormPopupTextWhenRequestTypeSelected
							: messages.createFormPopupTextWhenNoRequestTypeSelected,
					)}
				</PopupParagraph>
			</PopupContainer>
		),
		[formatMessage, isRequestTypeSelected],
	);

	return (
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		<div css={css({ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' })}>
			<Toggle
				isDisabled={isLoading || !isRequestTypeSelected}
				onChange={onToggleChanged}
				isChecked={isChecked}
				id="use-request-type-fields-toggle"
			/>
			<label
				// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css={css({
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					color: token('color.text.subtlest', colors.N90),
					fontSize: token('space.150', '12px'),
				})}
				htmlFor="use-request-type-fields-toggle"
			>
				{formatMessage(messages.toggleLabel)}
			</label>
			<Popup
				placement="right"
				shouldUseCaptureOnOutsideClick
				isOpen={isOpen}
				onClose={togglePopup}
				content={popupContent}
				zIndex={layers.tooltip}
				trigger={(triggerProps) => (
					<div
						// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						css={css({
							margin: 0,
							display: 'flex',
							flexDirection: 'row',
							paddingLeft: token('space.050', '4px'),
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
							span: {
								// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
								fontSize: '11px',
								fontWeight: 400,
							},
						})}
					>
						<Button {...triggerProps} appearance="subtle-link" spacing="none" onClick={togglePopup}>
							<span
								// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
								css={css({
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
									'-IconAccentColor': token('color.icon.accent.gray', colors.N200),
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
									'-IconColor': token('color.icon.accent.gray', colors.N100),
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
									color: isOpen ? 'var(--icon-accent-color)' : 'var(--icon-color)',
									'&:hover': {
										color: 'var(--icon-accent-color)',
									},
								})}
							>
								<EditorPanelIcon label="info" size="small" />
							</span>
						</Button>
					</div>
				)}
			/>
		</div>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PopupParagraph = styled.p<{
	children?: ReactNode;
}>({
	fontWeight: 400,
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '12px',
	lineHeight: '20px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const PopupContainer = styled.div<{
	children?: ReactNode;
}>({
	width: '280px',
	padding: token('space.200', '16px'),
});
